import { useCallback, useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  authService,
  type AuthResponse,
  type WaveSsoRedeemResponse,
} from '@/features/auth/services/auth.service';
import { WaveLinkAccount } from '@/features/auth/components/WaveLinkAccount';
import { parseSsoHash, type SsoHash } from '@/features/auth/utils/sso-hash';
import { useAuthStore } from '@/store/auth.store';
import { useBrandingStore } from '@/store/branding.store';
import { disconnectSocket } from '@/lib/socket';

export const Route = createFileRoute('/sso')({
  component: SsoPage,
});

// Fora do componente: o hash é limpo no 1º mount, então um remount (StrictMode, router)
// precisa reaproveitar o resgate em andamento em vez de ler hash vazio ou resgatar 2x.
let pendingSso: {
  sso: SsoHash;
  redeem: Promise<AuthResponse | WaveSsoRedeemResponse>;
} | null = null;

function takeSso() {
  if (pendingSso && !window.location.hash) return pendingSso;
  const sso = parseSsoHash(window.location.hash);
  // Tira o ticket/token da barra de endereço/histórico
  window.history.replaceState(null, '', window.location.pathname);
  if (!sso) return null;
  const redeem =
    sso.kind === 'wave'
      ? authService.redeemWaveSsoToken(sso.token)
      : authService.redeemWebSsoTicket(sso.ticket);
  pendingSso = { sso, redeem };
  return pendingSso;
}

/**
 * Entrada por link de uso único, que substitui qualquer sessão que já estivesse no
 * navegador (notebook compartilhado) pela do usuário que abriu o link:
 * - `/sso#ticket=...` — botão "Abrir Asseto" do Asseto Desktop;
 * - `/sso#wave=<jwt>[&next=/rota]` — Asseto aberto dentro do Wave. Sem vínculo da conta do
 *   Wave, o redeem não abre sessão: mostra o login + confirmação do vínculo.
 */
function SsoPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setAuth = useAuthStore((state) => state.setAuth);
  const logout = useAuthStore((state) => state.logout);
  const branding = useBrandingStore((state) => state.branding);
  const [failed, setFailed] = useState<'ticket' | 'wave' | null>(null);
  const [link, setLink] = useState<{
    linkTicket: string;
    emailHint: string;
    next: string | null;
  } | null>(null);

  const finish = useCallback(
    (response: AuthResponse, next: string | null) => {
      logout();
      disconnectSocket();
      queryClient.clear();
      setAuth(response.user, response.accessToken);
      if (next) navigate({ href: next, replace: true });
      else navigate({ to: '/my-dashboard', replace: true });
    },
    [logout, navigate, queryClient, setAuth],
  );

  useEffect(() => {
    const pending = takeSso();
    if (!pending) {
      setFailed('ticket');
      return;
    }
    const { sso, redeem } = pending;
    let active = true;

    redeem
      .then((response) => {
        if (!active) return;
        pendingSso = null;
        const next = sso.kind === 'wave' ? sso.next : null;
        if ('status' in response && response.status === 'link_required')
          setLink({ linkTicket: response.linkTicket, emailHint: response.emailHint, next });
        else finish(response, next);
      })
      .catch(() => {
        if (!active) return;
        pendingSso = null;
        setFailed(sso.kind);
      });
    return () => {
      active = false;
    };
  }, [finish]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      {link ? (
        <WaveLinkAccount
          linkTicket={link.linkTicket}
          emailHint={link.emailHint}
          onDone={(response, next) => finish(response, next ?? link.next)}
        />
      ) : failed ? (
        <div
          role="alert"
          className="w-full max-w-sm space-y-4 rounded-xl border bg-card p-6 text-center shadow-sm"
        >
          <p className="text-sm text-muted-foreground">
            {failed === 'wave'
              ? 'Não foi possível entrar pelo Wave. Feche e abra o Asseto de novo no Wave ou entre com seu e-mail e senha.'
              : 'O link de acesso expirou ou já foi usado. Clique de novo em "Abrir Asseto" no Asseto Desktop ou entre com seu e-mail e senha.'}
          </p>
          <Link to="/login" className="text-sm font-medium text-primary underline">
            Ir para o login
          </Link>
        </div>
      ) : (
        <div role="status" className="flex flex-col items-center gap-5">
          <div className="relative flex h-20 w-20 items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-primary/15 motion-safe:animate-ping" />
            <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-card shadow-md">
              {branding?.logoUrl ? (
                <img src={branding.logoUrl} alt="" className="h-9 w-9 object-contain" />
              ) : (
                <Loader2 className="h-7 w-7 text-primary motion-safe:animate-spin" />
              )}
            </span>
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            Entrando no {branding?.appName || 'Asseto'}...
          </p>
        </div>
      )}
    </div>
  );
}
